import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, RequestError, Service, SourcesMap, TemplateResolve } from 'webapp-types';
import { showTemplateCreationResult } from 'webapp/const/template-resolve';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import BoardPopupPreviewComponent from 'webapp/components/board/popup-preview';
import ReportsPopupPreviewComponent from 'webapp/components/reports/popup-preview';

import type ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';
import type Template from 'webapp/models-custom/template';

import { Severity } from 'webapp/services/logger';

export interface Args {
	model: ResolutionCenterItem;
	extraClasses?: string[];
}

export default class TemplatesTransferedNotificationBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('app-notifications') declare appNotificationsService: Service.AppNotifications;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/data-performance-summaries')
	declare mmDataPerformanceSummariesService: Service.ModelManager.DataPerformanceSummaries;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('account') declare accountService: Service.Account;
	@service('onboarding') declare onboardingService: Service.Onboarding;

	@tracked sourcesMap: SourcesMap = {};
	@tracked isRequestPending = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const model = this.args.model;

		if (model.params.isPublicTemplate) {
			if (this.isTransferedBoardType) {
				void this.mmTemplatesService.requestBoardPublicTemplates();
			} else if (this.isTransferedReportType) {
				void this.mmTemplatesService.requestReportPublicTemplates();
			}
		} else {
			if (this.isTransferedBoardType) {
				void this.mmTemplatesService.requestBoardAccountTemplates();
			} else if (this.isTransferedReportType) {
				void this.mmTemplatesService.requestReportAccountTemplates();
			}
		}
	}

	get classNames(): string {
		return ['notification', ...(this.args.extraClasses || []), 'filter-all'].join(' ');
	}

	get isTransferedBoardType(): boolean {
		return this.args.model.isTransferedBoardType;
	}

	get isTransferedReportType(): boolean {
		return this.args.model.isTransferedReportType;
	}

	get showPerformanceSummary(): boolean {
		return this.mmDataPerformanceSummariesService.showInUI;
	}

	@cached
	get templateModel(): Template | null {
		const model = this.args.model,
			objectId = this.isTransferedBoardType ? model.boardIds?.[0] : model.params.reportId;
		let templateModel: Template | null = null;

		if (objectId) {
			const templateRecord = this.mmTemplatesService.savedRecords.find(
				(item) => item.objectId === objectId,
			);

			if (templateRecord) {
				templateModel = this.mmTemplatesService.createModel(templateRecord);
			}
		}

		return templateModel;
	}

	get showLimitationWarning() {
		return !this.checkIfCan();
	}

	checkIfCan(openWarning = false) {
		const model = this.args.model,
			isPublicTemplate = !!model.params.isPublicTemplate,
			boardsCount = model.boardIds.length || 1;

		return (
			(this.isTransferedReportType &&
				this.mmReportsService.checkIfCanUseTemplate(
					isPublicTemplate,
					openWarning,
					boardsCount,
				)) ||
			(this.isTransferedBoardType &&
				this.billingService.checkIfCan(BillingFeature.BOARDS, openWarning, boardsCount))
		);
	}

	@action
	handleTransferedObject(isAccept: boolean): void {
		if (this.accountService.isDemoAccount) {
			return;
		}

		const model = this.args.model,
			isPublicTemplate = !!model.params.isPublicTemplate,
			objectTypeString = this.isTransferedBoardType ? 'Dashboard' : 'Report';

		const onAllResolved = (data?: TemplateResolve.NormalizedResolvePayload) => {
			const msgId = model.id!; // a message from BE

			if (isAlive(this)) {
				this.isRequestPending = true;
			}

			const layer = this.infolayerService.loading(
				isAccept
					? `Building ${objectTypeString.toLowerCase()}...`
					: `Deleting ${objectTypeString.toLowerCase()}...`,
			);

			this.appNotificationsService
				.resolveTemplateNotificationFromResolutionCenter(isAccept, msgId, data)
				.then((reply) => {
					if (!isAccept) {
						layer.ok(`${objectTypeString} deleted!`);
						return;
					}

					// accepting resolves with the created objects
					const result =
						reply as APIData.AppNotifications.ResolveTemplateNotificationFromResolutionCenterResponse;
					const boardData = result?.boards?.[0],
						reportData = (
							result as APIData.AppNotifications.ResolveReportTemplateNotificationFromResolutionCenterResponse
						)?.reports?.[0];
					let boardModel, reportModel;

					if (reportData) {
						reportModel = this.mmReportsService.normalReportModels.find(
							(item) => item.id === String(reportData.id),
						);
					} else if (boardData) {
						boardModel = this.mmBoardsService.normalBoardModels.find(
							(item) => item.id === String(boardData.id),
						);
					}

					if (!boardModel && !reportModel) {
						layer.error(`Error building ${objectTypeString.toLowerCase()}`);
						this.loggerService.logError(
							`[bgEvent] transfer ${objectTypeString}`,
							{
								msgId,
								response: JSON.stringify(result),
							},
							Severity.INFO,
						);

						return;
					}

					showTemplateCreationResult(layer, result, objectTypeString);

					if (this.isTransferedBoardType && boardModel) {
						boardModel.model.newBadge = true;

						this.mmBoardsService.triggerDataboardCreated(
							{ from: 'transfered_board' },
							boardModel,
						);
						this.onboardingService.triggerEventAllSetAfterFirstBoardCreated();
					}

					if (this.isTransferedReportType && reportModel) {
						reportModel.model.newBadge = true;

						this.analyticsService.event(TrackingEvent.REPORT_CREATED, {
							origin: 'transfered_report',
						});
						result.boards.forEach((board) => {
							const boardModel = this.mmBoardsService.normalBoardModels.find(
								(item) => item.id === String(board.id),
							);

							if (boardModel) {
								boardModel.model.newBadge = true;

								this.mmBoardsService.triggerDataboardCreated(
									{ from: 'transfered_report' },
									boardModel,
								);
							}
						});
					}

					if (isPublicTemplate) {
						// CASE: when pressing use template in preview board/report (that was opened from transfered notification element)
						const popupComponent = this.isTransferedBoardType
								? BoardPopupPreviewComponent
								: ReportsPopupPreviewComponent,
							popup = this.popupService.findByComponent(popupComponent);
						if (popup && 'template' in popup.data && popup.data.template) {
							void this.popupService.close(); // close preview popup
						}
					}
				})
				.finally(() => {
					this.isRequestPending = false;
				})
				.catch((error: RequestError) => {
					layer.close();
					this.requestService.handleGenericThrownError(error);
				});
		};

		const onReject = () => {
			onAllResolved();
		};

		if (isAccept) {
			if (this.checkIfCan(true)) {
				if (
					this.args.model.params.dataSourceTypeIds?.length ||
					this.args.model.params.hasDatasets
				) {
					this.mmTemplatesService.openResolvePopup(this.args.model, onAllResolved);
				} else {
					// CASE: When perform "Copy to client account" and there is no data source or there is customtoken used.
					onAllResolved();
				}
			}
		} else {
			if (isPublicTemplate) {
				this.popupService.openConfirmPopup({
					onConfirm: onReject,
					title: `Are you sure you want to remove this ${objectTypeString}?`,
					text: '',
					confirmAction: 'Remove',
					cancelAction: 'Cancel',
				});
			} else {
				onReject();
			}
		}
	}

	@action
	previewTemplate(): void {
		if (!this.templateModel) {
			return;
		}

		this.mmTemplatesService.openPreviewPopup(this.templateModel, () => {
			this.handleTransferedObject(true);
		});
	}
}
